import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from '../../hooks/useDebounce';
import { useListSearch } from '../../hooks/useListSearch';
import { useUserLocation } from '../../hooks/useUserLocation';
import SearchAutocomplete from '../../components/ui/SearchAutocomplete';
import ExploreSalonMap from '../../components/maps/ExploreSalonMap';
import MapAreaSearch from '../../components/maps/MapAreaSearch';
import { Link } from 'react-router-dom';
import {
  fetchDiscoverSalonSuggestions,
  fetchDiscoverSalons,
  fetchMarketplaceServices,
  fetchNearbySalons,
} from '../../api/discover';
import PaginatedFooter from '../../components/ui/PaginatedFooter';
import { DEFAULT_PAGE_SIZE } from '../../constants/pagination';
import ExploreLayout from '../../components/explore/ExploreLayout';
import BookmarkButton from '../../components/explore/BookmarkButton';
import SalonPreviewModal from '../../components/explore/SalonPreviewModal';
import AudienceBadge from '../../components/salon/AudienceBadge';
import { AUDIENCE_TYPES } from '../../constants/audienceTypes';
import { CATEGORIES } from '../../constants/serviceCategories';

const priceLabel = (level) => '₹'.repeat(level || 1);

const DEFAULT_MAP_RADIUS_KM = 10;
const MIN_MAP_RADIUS_KM = 1;
const MAX_MAP_RADIUS_KM = 50;

const ExplorePage = () => {
  const { t } = useTranslation();
  const {
    input: search,
    setInput: setSearch,
    appliedSearch,
    commitSearch,
    suggestions,
    suggestionsLoading,
  } = useListSearch({
    queryKeyPrefix: ['discover-salon-suggestions'],
    fetchSuggestions: (q) => fetchDiscoverSalonSuggestions({ q }),
  });
  const debouncedSearch = useDebounce(appliedSearch, 400);
  const [category, setCategory] = useState('All');
  const [city, setCity] = useState('');
  const [audienceType, setAudienceType] = useState('');
  const [priceLevel, setPriceLevel] = useState('');
  const [minRating, setMinRating] = useState(0);
  const [sort, setSort] = useState('popular');
  const [view, setView] = useState('grid');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [selectedPin, setSelectedPin] = useState(null);
  const [mapServiceId, setMapServiceId] = useState('');
  const [searchedAreaLabel, setSearchedAreaLabel] = useState('');
  const [mapRadiusKm, setMapRadiusKm] = useState(DEFAULT_MAP_RADIUS_KM);
  const [previewId, setPreviewId] = useState(null);
  const { location: mapCenter, loading: locationLoading, requestLocation, setLocation: setMapCenter } =
    useUserLocation(view === 'map');

  useEffect(() => {
    if (view === 'map') {
      setSearchedAreaLabel('');
    }
  }, [view]);

  const PRICE_LEVELS = [
    { value: '', label: t('explore.anyPrice') },
    { value: 1, label: `₹ ${t('explore.budget')}` },
    { value: 2, label: `₹₹ ${t('explore.midRange')}` },
    { value: 3, label: `₹₹₹ ${t('explore.premium')}` },
  ];

  const { data, isLoading } = useQuery({
    queryKey: ['discover-salons', debouncedSearch, category, city, audienceType, priceLevel, minRating, sort, page, pageSize],
    queryFn: () =>
      fetchDiscoverSalons({
        search: debouncedSearch || undefined,
        serviceCategory: category === 'All' ? undefined : category,
        city: city || undefined,
        audienceType: audienceType || undefined,
        priceLevel: priceLevel || undefined,
        minRating: minRating || undefined,
        sort,
        page,
        limit: pageSize,
      }),
  });

  const salons = data?.data || [];
  const pagination = data?.pagination;

  const { data: nearbyData, isLoading: nearbyLoading, isFetching: nearbyFetching } = useQuery({
    queryKey: ['nearby-salons', mapCenter?.lat, mapCenter?.lng, category, audienceType, mapServiceId, mapRadiusKm],
    queryFn: () =>
      fetchNearbySalons({
        lat: mapCenter.lat,
        lng: mapCenter.lng,
        radiusKm: mapRadiusKm,
        serviceCategory: category === 'All' ? undefined : category,
        audienceType: audienceType || undefined,
        serviceId: mapServiceId || undefined,
      }),
    enabled: view === 'map' && !!mapCenter?.lat && !!mapCenter?.lng,
  });

  const { data: servicesData } = useQuery({
    queryKey: ['map-services-filter'],
    queryFn: () => fetchMarketplaceServices({ limit: 200 }),
    enabled: view === 'map',
  });

  const mapSalons = nearbyData?.salons || [];
  const mapServices = useMemo(() => {
    const all = servicesData?.services || [];
    return category === 'All' ? all : all.filter((s) => s.category === category);
  }, [servicesData, category]);

  return (
    <ExploreLayout>
      <>
      <section className="border-b border-zinc-200 dark:border-zinc-800 bg-gradient-to-b from-glow-950/30 to-zinc-950 px-6 py-12">
        <div className="mx-auto max-w-3xl text-center">
          <h1 className="text-3xl font-bold md:text-4xl">{t('explore.heroTitle')}</h1>
          <p className="mt-2 text-zinc-600 dark:text-zinc-400">{t('explore.heroSubtitle')}</p>
          <SearchAutocomplete
            className="mt-8"
            inputClassName="rounded-2xl px-5 py-4 text-lg"
            value={search}
            onChange={setSearch}
            onSearch={(v) => { commitSearch(v); setPage(1); }}
            suggestions={suggestions}
            loading={suggestionsLoading}
            placeholder={t('explore.searchPlaceholder')}
          />
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-6 py-6">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setCategory('All');
              setMapServiceId('');
              setSelectedPin(null);
            }}
            className={`rounded-full px-3 py-1.5 text-xs ${category === 'All' ? 'bg-glow-600/30 text-glow-300' : 'bg-zinc-100 dark:bg-zinc-800'}`}
          >
            {t('common.all')}
          </button>
          {CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => {
                setCategory(c);
                setPage(1);
                setMapServiceId('');
                setSelectedPin(null);
              }}
              className={`rounded-full px-3 py-1.5 text-xs ${category === c ? 'bg-glow-600/30 text-glow-300' : 'bg-zinc-100 dark:bg-zinc-800'}`}
            >
              {c}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setFiltersOpen(!filtersOpen)}
            className="ml-auto rounded-lg border border-zinc-300 dark:border-zinc-700 px-3 py-1.5 text-xs"
          >
            {t('explore.advancedFilters')}
          </button>
          <div className="flex rounded-lg border border-zinc-200 dark:border-zinc-800 p-0.5">
            <button type="button" onClick={() => setView('grid')} className={`rounded px-3 py-1 text-xs ${view === 'grid' ? 'bg-zinc-700' : ''}`}>
              {t('common.gridView')}
            </button>
            <button type="button" onClick={() => setView('map')} className={`rounded px-3 py-1 text-xs ${view === 'map' ? 'bg-zinc-700' : ''}`}>
              {t('common.mapView')}
            </button>
          </div>
        </div>

        {filtersOpen && (
          <div className="mt-4 grid gap-4 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="text-xs text-zinc-600 dark:text-zinc-500">{t('explore.cityLocation')}</label>
              <input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="mt-1 w-full rounded border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm"
                placeholder={t('explore.cityPlaceholder')}
              />
            </div>
            <div>
              <label className="text-xs text-zinc-600 dark:text-zinc-500">{t('explore.audienceType')}</label>
              <select
                value={audienceType}
                onChange={(e) => {
                  setAudienceType(e.target.value);
                  setPage(1);
                }}
                className="mt-1 w-full rounded border border-zinc-300 bg-zinc-50 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-950"
              >
                <option value="">{t('explore.anyAudience')}</option>
                {AUDIENCE_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {t(`salon.audience.${type}`)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-zinc-600 dark:text-zinc-500">{t('explore.priceLevel')}</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {PRICE_LEVELS.filter((p) => p.value !== '').map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => setPriceLevel(String(p.value))}
                    className={`rounded-lg px-2 py-1 text-xs ${String(priceLevel) === String(p.value) ? 'bg-glow-600/30 text-glow-300' : 'bg-zinc-100 dark:bg-zinc-800'}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs text-zinc-600 dark:text-zinc-500">{t('explore.minRating')}</label>
              <div className="mt-2 flex gap-1">
                {[0, 1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setMinRating(n)}
                    className={`text-lg ${n <= minRating && n > 0 ? 'text-amber-400' : 'text-zinc-600 dark:text-zinc-500'}`}
                  >
                    {n === 0 ? t('common.all') : '★'}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs text-zinc-600 dark:text-zinc-500">{t('common.sortBy')}</label>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="mt-1 w-full rounded border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm"
              >
                <option value="popular">{t('explore.sortPopular')}</option>
                <option value="rating">{t('explore.sortRating')}</option>
                <option value="newest">{t('explore.sortNewest')}</option>
              </select>
            </div>
          </div>
        )}

        {isLoading && <p className="mt-8 text-zinc-600 dark:text-zinc-500">{t('common.loadingSalons')}</p>}

        {view === 'grid' && (
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {salons.map((salon) => (
              <article key={salon._id} className="overflow-hidden rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
                <div className="relative h-40 bg-zinc-100 dark:bg-zinc-800">
                  {salon.coverImage || salon.logo ? (
                    <img src={salon.coverImage || salon.logo} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full items-center justify-center text-zinc-600 dark:text-zinc-500">{t('common.noImage')}</div>
                  )}
                  <div className="absolute right-2 top-2">
                    <BookmarkButton tenantId={salon._id} size="sm" />
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-semibold">{salon.businessName}</h3>
                    <AudienceBadge audienceType={salon.audienceType} />
                  </div>
                  <p className="text-xs text-zinc-600 dark:text-zinc-500">{salon.city}</p>
                  <p className="mt-1 text-sm text-amber-400">
                    ★ {salon.averageRating || '—'} ({t('explore.reviewsCount', { count: salon.totalReviews || 0 })})
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {salon.topServices?.map((s) => (
                      <span key={s.name} className="rounded-full bg-zinc-100 dark:bg-zinc-800 px-2 py-0.5 text-[10px] text-zinc-600 dark:text-zinc-400">
                        {s.name}
                      </span>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-zinc-600 dark:text-zinc-500">{priceLabel(salon.priceLevel)}</p>
                  <Link
                    to={`/explore/${salon._id}`}
                    className="mt-4 block rounded-lg bg-glow-600 py-2 text-center text-sm text-white"
                  >
                    {t('explore.viewSalon')}
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}

        {view === 'map' && (
          <div className="mt-8 space-y-4">
            <div className="space-y-3 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
              <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{t('explore.chooseSearchArea')}</p>
              <div className="flex flex-wrap items-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setSearchedAreaLabel('');
                    requestLocation();
                    setSelectedPin(null);
                  }}
                  disabled={locationLoading}
                  className="rounded-lg bg-glow-600 px-3 py-2 text-sm text-white disabled:opacity-50"
                >
                  {locationLoading ? t('explore.locating') : t('explore.useMyLocation')}
                </button>
                <span className="pb-2 text-xs text-zinc-500">{t('common.or')}</span>
                <MapAreaSearch
                  onSelect={({ lat, lng, label }) => {
                    setMapCenter({ lat, lng });
                    setSearchedAreaLabel(label);
                    setSelectedPin(null);
                  }}
                />
              </div>
              {searchedAreaLabel && (
                <p className="text-xs text-zinc-500">
                  {t('explore.searchingNear', { place: searchedAreaLabel, km: mapRadiusKm })}
                </p>
              )}
              <div className="border-t border-zinc-200 pt-3 dark:border-zinc-800">
                <div className="flex items-center justify-between gap-3">
                  <label htmlFor="map-radius" className="text-xs font-medium text-zinc-600 dark:text-zinc-400">
                    {t('explore.searchRadius', { km: mapRadiusKm })}
                  </label>
                  <span className="text-xs text-zinc-500">
                    {MIN_MAP_RADIUS_KM}–{MAX_MAP_RADIUS_KM} km
                  </span>
                </div>
                <input
                  id="map-radius"
                  type="range"
                  min={MIN_MAP_RADIUS_KM}
                  max={MAX_MAP_RADIUS_KM}
                  step={1}
                  value={mapRadiusKm}
                  onChange={(e) => {
                    setMapRadiusKm(Number(e.target.value));
                    setSelectedPin(null);
                  }}
                  className="mt-2 w-full accent-glow-600"
                />
              </div>
              <div className="flex flex-wrap items-center gap-3 border-t border-zinc-200 pt-3 dark:border-zinc-800">
                <div className="min-w-0 w-full flex-1 sm:min-w-[200px]">
                  <label className="mb-1 block text-xs text-zinc-500">{t('explore.filterByService')}</label>
                  <select
                    value={mapServiceId}
                    onChange={(e) => {
                      setMapServiceId(e.target.value);
                      setSelectedPin(null);
                    }}
                    className="w-full rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm"
                  >
                    <option value="">{t('explore.allServices')}</option>
                    {mapServices.map((service) => (
                      <option key={service._id} value={service._id}>
                        {service.name} · {service.salonName}
                      </option>
                    ))}
                  </select>
                </div>
                <p className="text-sm text-zinc-600 dark:text-zinc-400">
                  {nearbyFetching ? t('explore.searchingNearby') : t('explore.salonCount', { count: mapSalons.length })}
                </p>
              </div>
            </div>

            {locationLoading || nearbyLoading ? (
              <p className="text-zinc-600 dark:text-zinc-500">{t('explore.loadingMap')}</p>
            ) : mapCenter ? (
              <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
                <ExploreSalonMap
                  center={mapCenter}
                  radiusKm={mapRadiusKm}
                  salons={mapSalons}
                  selectedSalonId={selectedPin}
                  onSelectSalon={setSelectedPin}
                  onCenterChange={(coords) => {
                    setMapCenter(coords);
                    setSelectedPin(null);
                  }}
                />
                <aside className="space-y-3 lg:max-h-[min(70vh,640px)] lg:overflow-y-auto">
                  {mapSalons.length === 0 ? (
                    <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6 text-center">
                      <p className="text-sm text-zinc-600 dark:text-zinc-400">{t('explore.noSalonsNearby', { km: mapRadiusKm })}</p>
                      <p className="mt-1 text-xs text-zinc-500">{t('explore.noSalonsNearbyHint')}</p>
                    </div>
                  ) : (
                    mapSalons.map((salon) => (
                      <div
                        key={salon._id}
                        className={`relative overflow-hidden rounded-xl border transition ${
                          selectedPin === salon._id
                            ? 'border-glow-500 bg-glow-600/10'
                            : 'border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 hover:border-glow-600/50'
                        }`}
                      >
                        <div className="absolute right-2 top-2 z-10">
                          <BookmarkButton tenantId={salon._id} size="sm" />
                        </div>
                        <button
                          type="button"
                          onClick={() => setSelectedPin(salon._id)}
                          className="w-full text-left"
                        >
                          <div className="flex gap-3 p-3 pr-12">
                            <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-zinc-100 dark:bg-zinc-800">
                              {salon.coverImage || salon.logo ? (
                                <img src={salon.coverImage || salon.logo} alt="" className="h-full w-full object-cover" />
                              ) : (
                                <div className="flex h-full items-center justify-center text-xs text-zinc-500">{t('common.noImage')}</div>
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="truncate font-medium">{salon.businessName}</p>
                                <AudienceBadge audienceType={salon.audienceType} />
                              </div>
                              {salon.phone && <p className="truncate text-xs text-zinc-500">{salon.phone}</p>}
                              <p className="mt-1 text-xs text-amber-500">
                                ★ {salon.averageRating || '—'} · {t('explore.distanceAway', { km: salon.distanceKm })}
                              </p>
                            </div>
                          </div>
                        </button>
                        <div className="mx-3 mb-3 flex gap-2">
                          <button
                            type="button"
                            onClick={() => setPreviewId(salon._id)}
                            className="flex-1 rounded-lg border border-zinc-300 py-2 text-center text-xs font-medium dark:border-zinc-700"
                          >
                            {t('client.bookmarks.openPreview')}
                          </button>
                          <Link
                            to={`/explore/${salon._id}`}
                            className="flex-1 rounded-lg bg-glow-600 py-2 text-center text-xs font-medium text-white hover:bg-glow-500"
                          >
                            {t('explore.viewSalon')}
                          </Link>
                        </div>
                      </div>
                    ))
                  )}
                </aside>
              </div>
            ) : (
              <p className="text-zinc-600 dark:text-zinc-500">{t('explore.locationRequired')}</p>
            )}

          </div>
        )}

        {view === 'grid' && (
          <PaginatedFooter
            pagination={pagination}
            page={page}
            onPageChange={setPage}
            pageSize={pageSize}
            onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
          />
        )}
      </div>
      <SalonPreviewModal tenantId={previewId} open={!!previewId} onClose={() => setPreviewId(null)} />
      </>
    </ExploreLayout>
  );
};

export default ExplorePage;
